import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let loadPromise = null;

function ensureTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) {
    return Promise.resolve();
  }
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/**
 * Cloudflare Turnstile widget. If siteKey is missing, renders nothing and calls onToken(null).
 * @param {string|null|undefined} siteKey - REACT_APP_TURNSTILE_SITE_KEY
 * @param {(token: string|null) => void} onToken - latest response token, or null if reset/expired/error
 */
export function TurnstileWidget({ siteKey, onToken, className }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    const key = (siteKey || '').trim();
    if (!key) {
      onTokenRef.current?.(null);
      return undefined;
    }

    let cancelled = false;

    ensureTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: key,
          size: 'compact',
          callback: (token) => {
            if (!cancelled) onTokenRef.current?.(token || null);
          },
          'error-callback': () => {
            if (!cancelled) onTokenRef.current?.(null);
          },
          'expired-callback': () => {
            if (!cancelled) onTokenRef.current?.(null);
          },
        });
      })
      .catch(() => {
        if (!cancelled) onTokenRef.current?.(null);
      });

    return () => {
      cancelled = true;
      const wid = widgetIdRef.current;
      widgetIdRef.current = null;
      if (wid != null && window.turnstile?.remove) {
        try {
          window.turnstile.remove(wid);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey]);

  const key = (siteKey || '').trim();
  if (!key) {
    return null;
  }

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
}
